import { useEffect, useRef, useState } from "react";

type TurnstileApi = {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let turnstileLoader: Promise<TurnstileApi> | undefined;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileLoader) return turnstileLoader;

  turnstileLoader = new Promise<TurnstileApi>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>("script[data-turnstile-script]");
    const script = existingScript ?? document.createElement("script");

    const handleLoad = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("Turnstile loaded without exposing its API."));
    };

    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", () => reject(new Error("Turnstile could not be loaded.")), { once: true });

    if (!existingScript) {
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.defer = true;
      script.dataset.turnstileScript = "true";
      document.head.append(script);
    }
  });

  return turnstileLoader;
}

type TurnstileWidgetProps = {
  siteKey: string;
  language: "en" | "it";
  onToken: (token: string | null) => void;
  onError: () => void;
};

export function TurnstileWidget({ siteKey, language, onToken, onError }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [interactionRequired, setInteractionRequired] = useState(false);

  useEffect(() => {
    let disposed = false;
    let widgetId: string | undefined;

    void loadTurnstile()
      .then((turnstile) => {
        if (disposed || !containerRef.current) return;
        widgetId = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: "contact",
          appearance: "interaction-only",
          language,
          size: "flexible",
          theme: "light",
          callback: (token: string) => onToken(token),
          "before-interactive-callback": () => setInteractionRequired(true),
          "after-interactive-callback": () => setInteractionRequired(false),
          "expired-callback": () => {
            setInteractionRequired(false);
            onToken(null);
          },
          "error-callback": () => {
            setInteractionRequired(false);
            onToken(null);
            onError();
          },
        });
      })
      .catch(() => {
        if (!disposed) onError();
      });

    return () => {
      disposed = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [language, onError, onToken, siteKey]);

  return <div className={`contact-turnstile${interactionRequired ? " is-visible" : ""}`} ref={containerRef} />;
}
